/**
 * FeatureRequestService - Handles all feature request operations
 *
 * Provides comprehensive CRUD operations for feature requests, comments,
 * activity tracking, screenshot uploads, and analytics.
 *
 * @module services/FeatureRequestService
 */

import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  // deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  // Timestamp,
  QueryConstraint,
  increment,
  writeBatch,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import {
  FeatureRequest,
  RequestFormData,
  RequestFilters,
  RequestComment,
  RequestActivity,
  RequestAnalytics,
  RequestStatus,
  RequestType,
  Screenshot,
  DeveloperPriority,
  VALIDATION_RULES,

} from '../types/featureRequest';
import { ValidationResult } from '../types/common';

/**
 * FeatureRequestService - Static service class for feature request operations
 */
export class FeatureRequestService {
  private static readonly REQUESTS_COLLECTION = 'featureRequests';
  private static readonly COMMENTS_COLLECTION = 'requestComments';
  private static readonly ACTIVITY_COLLECTION = 'requestActivity';
  private static readonly STORAGE_PATH = 'feature-requests';

  // ==================== USER OPERATIONS ====================

  /**
   * Create a new feature request
   */
  static async createRequest(
    formData: RequestFormData,
    userId?: string,
    userName?: string
  ): Promise<FeatureRequest> {
    try {
      // Validate form data
      const validation = this.validateRequestFormData(formData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
      }

      // Create request document
      const requestData = {
        type: formData.type,
        title: formData.title.trim(),
        description: formData.description.trim(),
        userPriority: formData.userPriority,
        screenshots: [],
        submittedBy: userId || 'anonymous',
        submittedByName: userName || 'Anonymous User',
        submittedByEmail: formData.email?.trim(),
        status: 'new' as RequestStatus,
        commentCount: 0,
        screenshotCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Generate a new document reference to get the ID first
      const docRef = doc(collection(db, this.REQUESTS_COLLECTION));

      // Add to Firestore with ID included
      await setDoc(docRef, {
        ...requestData,
        id: docRef.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Upload screenshots if provided
      let screenshots: Screenshot[] = [];
      if (formData.screenshots && formData.screenshots.length > 0) {
        screenshots = await this.uploadScreenshots(docRef.id, formData.screenshots);
        await updateDoc(docRef, {
          screenshots,
          screenshotCount: screenshots.length,
        });
      }

      // Log activity
      await this.logActivity({
        requestId: docRef.id,
        type: 'creation',
        description: `Request created: "${formData.title}"`,
        performedBy: userId || 'anonymous',
        performedByName: userName || 'Anonymous User',
        performedByRole: userId ? 'user' : 'user',
      });

      return {
        ...requestData,
        id: docRef.id,
        screenshots,
        screenshotCount: screenshots.length,
      } as FeatureRequest;
    } catch (error: any) {
      console.error('Error creating feature request:', error);
      throw new Error(`Failed to create request: ${error.message}`);
    }
  }

  /**
   * Get requests by user (email or userId)
   */
  static async getMyRequests(userIdentifier: string): Promise<FeatureRequest[]> {
    try {
      const constraints: QueryConstraint[] = [];

      // Try to match by userId or email
      if (userIdentifier.includes('@')) {
        constraints.push(where('submittedByEmail', '==', userIdentifier));
      } else {
        constraints.push(where('submittedBy', '==', userIdentifier));
      }

      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, this.REQUESTS_COLLECTION), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => this.firestoreDocToRequest(doc.data()));
    } catch (error: any) {
      console.error('Error fetching user requests:', error);
      throw new Error(`Failed to fetch requests: ${error.message}`);
    }
  }

  /**
   * Get a single request by ID
   */
  static async getRequestById(requestId: string): Promise<FeatureRequest | null> {
    try {
      const docRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.firestoreDocToRequest(docSnap.data());
    } catch (error: any) {
      console.error('Error fetching request:', error);
      throw new Error(`Failed to fetch request: ${error.message}`);
    }
  }

  /**
   * Add a comment to a request
   */
  static async addComment(
    requestId: string,
    content: string,
    authorId: string,
    authorName: string,
    authorRole: 'user' | 'developer' | 'admin',
    isPrivate: boolean = false
  ): Promise<RequestComment> {
    try {
      // Validate comment
      if (content.trim().length < VALIDATION_RULES.comment.minLength) {
        throw new Error('Comment is too short');
      }
      if (content.trim().length > VALIDATION_RULES.comment.maxLength) {
        throw new Error('Comment is too long');
      }

      const commentData = {
        requestId,
        authorId,
        authorName,
        authorRole,
        content: content.trim(),
        isPrivate,
        isEdited: false,
        createdAt: new Date().toISOString(),
      };

      // Add comment
      const docRef = await addDoc(
        collection(db, this.COMMENTS_COLLECTION),
        {
          ...commentData,
          createdAt: serverTimestamp(),
        }
      );

      await updateDoc(docRef, { id: docRef.id });

      // Increment comment count
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        commentCount: increment(1),
        updatedAt: serverTimestamp(),
      });

      // Log activity
      await this.logActivity({
        requestId,
        type: 'comment',
        description: `${authorName} added a comment`,
        performedBy: authorId,
        performedByName: authorName,
        performedByRole: authorRole,
      });

      return {
        ...commentData,
        id: docRef.id,
      } as RequestComment;
    } catch (error: any) {
      console.error('Error adding comment:', error);
      throw new Error(`Failed to add comment: ${error.message}`);
    }
  }

  /**
   * Get comments for a request
   */
  static async getComments(
    requestId: string,
    includePrivate: boolean = false
  ): Promise<RequestComment[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('requestId', '==', requestId),
        orderBy('createdAt', 'asc'),
      ];

      if (!includePrivate) {
        constraints.push(where('isPrivate', '==', false));
      }

      const q = query(collection(db, this.COMMENTS_COLLECTION), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
        } as RequestComment;
      });
    } catch (error: any) {
      console.error('Error fetching comments:', error);
      throw new Error(`Failed to fetch comments: ${error.message}`);
    }
  }

  /**
   * Upload screenshots for a request
   */
  static async uploadScreenshots(
    requestId: string,
    files: File[]
  ): Promise<Screenshot[]> {
    try {
      const screenshots: Screenshot[] = [];

      for (let i = 0; i < Math.min(files.length, VALIDATION_RULES.screenshots.maxCount); i++) {
        const file = files[i];

        // Validate file
        if (!VALIDATION_RULES.screenshots.allowedTypes.includes(file.type)) {
          throw new Error(`Invalid file type: ${file.type}`);
        }
        if (file.size > VALIDATION_RULES.screenshots.maxSizeBytes) {
          throw new Error(`File too large: ${file.name}`);
        }

        // Generate unique filename
        const timestamp = Date.now();
        const fileName = `${timestamp}-${i}-${file.name}`;
        const storagePath = `${this.STORAGE_PATH}/${requestId}/screenshots/${fileName}`;

        // Upload to Firebase Storage
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);

        // Get download URL
        const url = await getDownloadURL(storageRef);

        screenshots.push({
          id: `${requestId}-${timestamp}-${i}`,
          url,
          storagePath,
          fileName: file.name,
          fileSize: file.size,
          uploadedAt: new Date().toISOString(),
        });
      }

      // Log activity
      await this.logActivity({
        requestId,
        type: 'screenshot_added',
        description: `${screenshots.length} screenshot(s) added`,
        performedBy: 'system',
        performedByName: 'System',
        performedByRole: 'user',
      });

      return screenshots;
    } catch (error: any) {
      console.error('Error uploading screenshots:', error);
      throw new Error(`Failed to upload screenshots: ${error.message}`);
    }
  }

  /**
   * Upload additional screenshot to existing request
   */
  static async uploadScreenshot(
    requestId: string,
    file: File
  ): Promise<Screenshot> {
    try {
      const request = await this.getRequestById(requestId);
      if (!request) {
        throw new Error('Request not found');
      }

      if (request.screenshots.length >= VALIDATION_RULES.screenshots.maxCount) {
        throw new Error(`Maximum ${VALIDATION_RULES.screenshots.maxCount} screenshots allowed`);
      }

      const screenshots = await this.uploadScreenshots(requestId, [file]);
      const newScreenshot = screenshots[0];

      // Update request with new screenshot
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        screenshots: [...request.screenshots, newScreenshot],
        screenshotCount: increment(1),
        updatedAt: serverTimestamp(),
      });

      return newScreenshot;
    } catch (error: any) {
      console.error('Error uploading screenshot:', error);
      throw new Error(`Failed to upload screenshot: ${error.message}`);
    }
  }

  // ==================== DEVELOPER OPERATIONS ====================

  /**
   * Get all requests with optional filters
   */
  static async getAllRequests(filters?: RequestFilters): Promise<FeatureRequest[]> {
    try {
      const constraints: QueryConstraint[] = [];

      // Apply filters
      if (filters?.status) {
        if (Array.isArray(filters.status)) {
          constraints.push(where('status', 'in', filters.status));
        } else {
          constraints.push(where('status', '==', filters.status));
        }
      }

      if (filters?.type) {
        if (Array.isArray(filters.type)) {
          constraints.push(where('type', 'in', filters.type));
        } else {
          constraints.push(where('type', '==', filters.type));
        }
      }

      if (filters?.submittedBy) {
        constraints.push(where('submittedBy', '==', filters.submittedBy));
      }

      // Sorting
      const sortBy = filters?.sortBy || 'createdAt';
      const sortOrder = filters?.sortOrder || 'desc';
      constraints.push(orderBy(sortBy, sortOrder));

      // Limit
      if (filters?.limit) {
        constraints.push(firestoreLimit(filters.limit));
      }

      const q = query(collection(db, this.REQUESTS_COLLECTION), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => this.firestoreDocToRequest(doc.data()));
    } catch (error: any) {
      console.error('Error fetching requests:', error);
      throw new Error(`Failed to fetch requests: ${error.message}`);
    }
  }

  /**
   * Update request status
   */
  static async updateRequestStatus(
    requestId: string,
    status: RequestStatus,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);

      const updateData: any = {
        status,
        updatedAt: serverTimestamp(),
      };

      // Set completedAt if status is completed
      if (status === 'completed') {
        updateData.completedAt = serverTimestamp();
      }

      await updateDoc(requestRef, updateData);

      // Log activity
      await this.logActivity({
        requestId,
        type: 'status_change',
        description: `Status changed to: ${status}`,
        performedBy: userId,
        performedByName: userName,
        performedByRole: 'developer',
        metadata: { newStatus: status },
      });
    } catch (error: any) {
      console.error('Error updating status:', error);
      throw new Error(`Failed to update status: ${error.message}`);
    }
  }

  /**
   * Update developer priority
   */
  static async updateDeveloperPriority(
    requestId: string,
    priority: DeveloperPriority,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        developerPriority: priority,
        updatedAt: serverTimestamp(),
      });

      // Log activity
      await this.logActivity({
        requestId,
        type: 'priority_change',
        description: `Priority set to: ${priority}`,
        performedBy: userId,
        performedByName: userName,
        performedByRole: 'developer',
        metadata: { newPriority: priority },
      });
    } catch (error: any) {
      console.error('Error updating priority:', error);
      throw new Error(`Failed to update priority: ${error.message}`);
    }
  }

  /**
   * Add developer note (private)
   */
  static async addDeveloperNote(
    requestId: string,
    note: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        developerNotes: note.trim(),
        updatedAt: serverTimestamp(),
      });

      // Log activity
      await this.logActivity({
        requestId,
        type: 'assignment',
        description: `Developer note added`,
        performedBy: userId,
        performedByName: userName,
        performedByRole: 'developer',
      });
    } catch (error: any) {
      console.error('Error adding developer note:', error);
      throw new Error(`Failed to add developer note: ${error.message}`);
    }
  }

  /**
   * Add public response (visible to user)
   */
  static async addPublicResponse(
    requestId: string,
    response: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        publicResponse: response.trim(),
        updatedAt: serverTimestamp(),
      });

      // Log activity
      await this.logActivity({
        requestId,
        type: 'comment',
        description: `Developer added a public response`,
        performedBy: userId,
        performedByName: userName,
        performedByRole: 'developer',
      });
    } catch (error: any) {
      console.error('Error adding public response:', error);
      throw new Error(`Failed to add public response: ${error.message}`);
    }
  }

  /**
   * Mark request as duplicate
   */
  static async markAsDuplicate(
    requestId: string,
    originalRequestId: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        status: 'duplicate' as RequestStatus,
        linkedRequestId: originalRequestId,
        updatedAt: serverTimestamp(),
      });

      // Log activity
      await this.logActivity({
        requestId,
        type: 'status_change',
        description: `Marked as duplicate of request #${originalRequestId}`,
        performedBy: userId,
        performedByName: userName,
        performedByRole: 'developer',
        metadata: { originalRequestId },
      });
    } catch (error: any) {
      console.error('Error marking as duplicate:', error);
      throw new Error(`Failed to mark as duplicate: ${error.message}`);
    }
  }

  /**
   * Assign request to milestone
   */
  static async assignToMilestone(
    requestId: string,
    milestone: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        assignedToMilestone: milestone,
        updatedAt: serverTimestamp(),
      });

      // Log activity
      await this.logActivity({
        requestId,
        type: 'assignment',
        description: `Assigned to milestone: ${milestone}`,
        performedBy: userId,
        performedByName: userName,
        performedByRole: 'developer',
        metadata: { milestone },
      });
    } catch (error: any) {
      console.error('Error assigning to milestone:', error);
      throw new Error(`Failed to assign to milestone: ${error.message}`);
    }
  }

  // ==================== ACTIVITY & ANALYTICS ====================

  /**
   * Log activity for a request
   */
  private static async logActivity(
    activity: Omit<RequestActivity, 'id' | 'createdAt'>
  ): Promise<void> {
    try {
      const activityData = {
        ...activity,
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(
        collection(db, this.ACTIVITY_COLLECTION),
        activityData
      );

      await updateDoc(docRef, { id: docRef.id });
    } catch (error: any) {
      console.error('Error logging activity:', error);
      // Don't throw - activity logging is not critical
    }
  }

  /**
   * Get activity log for a request
   */
  static async getActivity(requestId: string): Promise<RequestActivity[]> {
    try {
      const q = query(
        collection(db, this.ACTIVITY_COLLECTION),
        where('requestId', '==', requestId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
        } as RequestActivity;
      });
    } catch (error: any) {
      console.error('Error fetching activity:', error);
      throw new Error(`Failed to fetch activity: ${error.message}`);
    }
  }

  /**
   * Get analytics data
   */
  static async getAnalytics(): Promise<RequestAnalytics> {
    try {
      const allRequests = await this.getAllRequests();

      // Calculate statistics
      const totalRequests = allRequests.length;

      const byType = allRequests.reduce((acc, req) => {
        acc[req.type] = (acc[req.type] || 0) + 1;
        return acc;
      }, {} as Record<RequestType, number>);

      const byStatus = allRequests.reduce((acc, req) => {
        acc[req.status] = (acc[req.status] || 0) + 1;
        return acc;
      }, {} as Record<RequestStatus, number>);

      const byUserPriority = allRequests.reduce((acc, req) => {
        acc[req.userPriority] = (acc[req.userPriority] || 0) + 1;
        return acc;
      }, {} as any);

      const byDeveloperPriority = allRequests.reduce((acc, req) => {
        if (req.developerPriority) {
          acc[req.developerPriority] = (acc[req.developerPriority] || 0) + 1;
        }
        return acc;
      }, {} as any);

      // Calculate average resolution time
      const completedRequests = allRequests.filter(req => req.completedAt);
      const totalResolutionTime = completedRequests.reduce((sum, req) => {
        const created = new Date(req.createdAt).getTime();
        const completed = new Date(req.completedAt!).getTime();
        return sum + (completed - created);
      }, 0);
      const averageResolutionTime = completedRequests.length > 0
        ? totalResolutionTime / completedRequests.length / (1000 * 60 * 60 * 24) // Convert to days
        : 0;

      // Most active users
      const userCounts = allRequests.reduce((acc, req) => {
        const key = `${req.submittedBy}|${req.submittedByName}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const mostActiveUsers = Object.entries(userCounts)
        .map(([key, count]) => {
          const [userId, userName] = key.split('|');
          return { userId, userName, requestCount: count };
        })
        .sort((a, b) => b.requestCount - a.requestCount)
        .slice(0, 5);

      // Recent activity
      const recentActivity = await this.getRecentActivity(10);

      // Completion rate
      const completedCount = byStatus['completed'] || 0;
      const completionRate = totalRequests > 0
        ? (completedCount / totalRequests) * 100
        : 0;

      // Response rate
      const requestsWithResponse = allRequests.filter(
        req => req.publicResponse || req.commentCount > 0
      ).length;
      const responseRate = totalRequests > 0
        ? (requestsWithResponse / totalRequests) * 100
        : 0;

      return {
        totalRequests,
        byType,
        byStatus,
        byUserPriority,
        byDeveloperPriority,
        averageResolutionTime,
        mostActiveUsers,
        recentActivity,
        completionRate,
        responseRate,
      };
    } catch (error: any) {
      console.error('Error getting analytics:', error);
      throw new Error(`Failed to get analytics: ${error.message}`);
    }
  }

  /**
   * Get recent activity across all requests
   */
  static async getRecentActivity(limit: number = 20): Promise<RequestActivity[]> {
    try {
      const q = query(
        collection(db, this.ACTIVITY_COLLECTION),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limit)
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
        } as RequestActivity;
      });
    } catch (error: any) {
      console.error('Error fetching recent activity:', error);
      throw new Error(`Failed to fetch recent activity: ${error.message}`);
    }
  }

  // ==================== UTILITY FUNCTIONS ====================

  /**
   * Validate request form data
   */
  private static validateRequestFormData(formData: RequestFormData): ValidationResult {
    const errors: Record<string, string> = {};

    // Validate title
    if (!formData.title || formData.title.trim().length < VALIDATION_RULES.title.minLength) {
      errors.title = `Title must be at least ${VALIDATION_RULES.title.minLength} characters`;
    }
    if (formData.title && formData.title.length > VALIDATION_RULES.title.maxLength) {
      errors.title = `Title must be at most ${VALIDATION_RULES.title.maxLength} characters`;
    }

    // Validate description
    if (!formData.description || formData.description.trim().length < VALIDATION_RULES.description.minLength) {
      errors.description = `Description must be at least ${VALIDATION_RULES.description.minLength} characters`;
    }
    if (formData.description && formData.description.length > VALIDATION_RULES.description.maxLength) {
      errors.description = `Description must be at most ${VALIDATION_RULES.description.maxLength} characters`;
    }

    // Validate type
    if (!formData.type) {
      errors.type = 'Type is required';
    }

    // Validate priority
    if (!formData.userPriority) {
      errors.userPriority = 'Priority is required';
    }

    // Validate email if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Convert Firestore document to FeatureRequest
   */
  private static firestoreDocToRequest(data: any): FeatureRequest {
    return {
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      completedAt: data.completedAt?.toDate?.()?.toISOString() || data.completedAt,
    } as FeatureRequest;
  }

  /**
   * Delete a screenshot
   */
  static async deleteScreenshot(
    requestId: string,
    screenshotId: string
  ): Promise<void> {
    try {
      const request = await this.getRequestById(requestId);
      if (!request) {
        throw new Error('Request not found');
      }

      const screenshot = request.screenshots.find(s => s.id === screenshotId);
      if (!screenshot) {
        throw new Error('Screenshot not found');
      }

      // Delete from storage
      const storageRef = ref(storage, screenshot.storagePath);
      await deleteObject(storageRef);

      // Update request
      const updatedScreenshots = request.screenshots.filter(s => s.id !== screenshotId);
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        screenshots: updatedScreenshots,
        screenshotCount: updatedScreenshots.length,
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error deleting screenshot:', error);
      throw new Error(`Failed to delete screenshot: ${error.message}`);
    }
  }

  /**
   * Delete a request (admin only)
   */
  static async deleteRequest(requestId: string): Promise<void> {
    try {
      const batch = writeBatch(db);

      // Delete request document
      const requestRef = doc(db, this.REQUESTS_COLLECTION, requestId);
      batch.delete(requestRef);

      // Delete all comments
      const commentsQuery = query(
        collection(db, this.COMMENTS_COLLECTION),
        where('requestId', '==', requestId)
      );
      const commentsSnapshot = await getDocs(commentsQuery);
      commentsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Delete all activity
      const activityQuery = query(
        collection(db, this.ACTIVITY_COLLECTION),
        where('requestId', '==', requestId)
      );
      const activitySnapshot = await getDocs(activityQuery);
      activitySnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();

      // Note: Screenshots in storage are not deleted automatically
      // Consider implementing a cleanup function or using Firebase Storage Rules
    } catch (error: any) {
      console.error('Error deleting request:', error);
      throw new Error(`Failed to delete request: ${error.message}`);
    }
  }
}
